import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { PersonIdentityVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { personScreenLink } from '@/features/dashboard/bookingChanges/routes/detailsLinks.ts';

/** Uczestnik dostaje mocniejszy nagłówek — ma pod sobą rachunek, zapisujący tylko kontakt. */
export type PersonTileIdentitySize = 'md' | 'lg';

const nameVariants = cva('font-bold text-ink', {
  variants: {
    size: {
      md: 'text-base',
      lg: 'text-[17px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

interface PersonTileIdentityProps extends React.HTMLAttributes<HTMLDivElement> {
  person: PersonIdentityVm;
  /** Fakty pod nazwiskiem — wiek, kontakt, liczba zmian w szkicu. */
  meta?: string[];
  /** Odznaka przy nazwisku — dziś wyłącznie rezygnacja uczestnika. */
  badge?: React.ReactNode;
  isCurrent?: boolean;
  size?: PersonTileIdentitySize;
}

const PersonTileIdentity = ({
  person,
  meta,
  badge,
  isCurrent = false,
  size = 'md',
  className,
  ...props
}: PersonTileIdentityProps) => {
  const link = isCurrent ? undefined : personScreenLink(person.kind, person.id);

  return (
    <div className={cn('min-w-0', className)} {...props}>
      <div className="flex flex-wrap items-baseline gap-x-[9px]">
        {link ? (
          <Link
            to={link.to}
            params={link.params}
            search={(prev) => prev}
            onClick={(event) => event.stopPropagation()}
            className={cn(
              nameVariants({ size }),
              'underline-offset-2 hover:underline',
            )}
          >
            {person.fullName}&nbsp;
            <ArrowUpRight
              aria-hidden
              className="inline size-3.5 shrink-0 align-[-0.1em] text-neutral-400"
            />
          </Link>
        ) : (
          <span className={nameVariants({ size })}>{person.fullName}</span>
        )}
        <span className="whitespace-nowrap text-[13px] text-neutral-500">
          {person.roleLabel}
        </span>
        {badge}
      </div>

      {!!meta?.length && (
        <div className="flex flex-wrap items-baseline gap-x-[5px] text-[13px] text-neutral-500">
          {meta.map((fact, index) => (
            <span key={`${index}-${fact}`} className="whitespace-nowrap">
              {index > 0 && (
                <span aria-hidden className="mr-[5px]">
                  ·
                </span>
              )}
              {fact}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PersonTileIdentity;
